import React, { forwardRef, memo } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'components';
import { Icon } from '../../Icon/Icon';
import { MessageRenderer } from '../MessageRenderer';

/**
 * ChatMessages - Message list component for Gigabot
 *
 * Props:
 * - messages: array - List of message objects
 * - formatTime: function - Utility to format timestamps
 * - activeDropdown: string|number - ID of message with open dropdown
 * - onToggleDropdown: function - Callback to toggle message dropdown
 * - onDeleteMessage: function - Callback to delete a message
 * - onButtonClick: function - Callback for button clicks in messages
 * - dropdownRef: ref - Ref for dropdown element
 * - showActionProposals: boolean - Whether action proposals feature is enabled
 * - autoRunMode: string - Current auto-run mode
 * - onActionRun: function - Callback when action is run
 * - onActionSkip: function - Callback when action is skipped
 * - getProposal: function - Get proposal by ID
 */
export const ChatMessages = forwardRef(
  (
    {
      messages = [],
      formatTime,
      activeDropdown,
      onToggleDropdown,
      onDeleteMessage,
      onButtonClick,
      dropdownRef,
      showActionProposals = false,
      autoRunMode = 'off',
      onActionRun,
      onActionSkip,
      getProposal,
    },
    messagesEndRef
  ) => {
    return (
      <div
        className='gigabot-messages'
        role='log'
        aria-live='polite'
        aria-atomic='false'
        aria-label='Chat messages'
      >
        {messages.map((message) => (
          <div
            key={message.id}
            className={`gigabot-message ${
              message.type === 'user' ? 'user' : 'bot'
            }`}
          >
            <div className='gigabot-message-meta'>
              <div className='meta-left'>
                <span className='sender'>
                  {message.type === 'user' ? '> you' : '> gigabot'}
                </span>
                <span className='time'>{formatTime(message.timestamp)}</span>
              </div>
              {message.type === 'assistant' && !message.isStreaming && (
                <div className='message-actions' ref={dropdownRef}>
                  <Button
                    className='action-button'
                    onClick={() => onToggleDropdown(message.id)}
                    aria-label='Message actions'
                    icon={<Icon name='More_Vertical' size='md' />}
                  />
                  {activeDropdown === message.id && (
                    <div className='action-dropdown'>
                      <Button
                        className='dropdown-item delete'
                        onClick={() => onDeleteMessage(message.id)}
                        icon={<Icon name='Trash_Empty' size='md' />}
                      >
                        Delete
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className='gigabot-message-content'>
              {message.isStreaming ? (
                <div className='typing-indicator'>
                  <span>gigacore is thinking</span>
                  <div className='typing-dots'>
                    <span className='dot'></span>
                    <span className='dot'></span>
                    <span className='dot'></span>
                  </div>
                  {message.content && (
                    <div className='streaming-content'>
                      <MessageRenderer
                        content={message.content}
                        structuredData={message.structuredData}
                        buttons={message.buttons}
                        onButtonClick={onButtonClick}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className='final-content'>
                  {message.content && (
                    <>
                      <MessageRenderer
                        content={message.content}
                        structuredData={message.structuredData}
                        buttons={message.buttons}
                        onButtonClick={onButtonClick}
                        actionProposal={
                          showActionProposals ? message.actionProposal : null
                        }
                        onActionRun={onActionRun}
                        onActionSkip={onActionSkip}
                        actionStatus={
                          message.actionProposal
                            ? getProposal(message.actionProposal.id)?.status ||
                              'pending'
                            : 'pending'
                        }
                        autoRunMode={autoRunMode}
                      />
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
    );
  }
);

ChatMessages.displayName = 'ChatMessages';

ChatMessages.propTypes = {
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      type: PropTypes.oneOf(['user', 'assistant']).isRequired,
      content: PropTypes.string,
      timestamp: PropTypes.instanceOf(Date),
      isStreaming: PropTypes.bool,
      structuredData: PropTypes.object,
      buttons: PropTypes.array,
      actionProposal: PropTypes.object,
    })
  ),
  formatTime: PropTypes.func.isRequired,
  activeDropdown: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onToggleDropdown: PropTypes.func.isRequired,
  onDeleteMessage: PropTypes.func.isRequired,
  onButtonClick: PropTypes.func,
  dropdownRef: PropTypes.object,
  showActionProposals: PropTypes.bool,
  autoRunMode: PropTypes.string,
  onActionRun: PropTypes.func,
  onActionSkip: PropTypes.func,
  getProposal: PropTypes.func,
};

export default memo(ChatMessages);
